/**
 * pages/team-detail.ts —— 队伍详情（W2-4）
 * 来历：一期 [SEC:page-team-detail] 的 renderTeamDetail()，版式不变。
 *
 * ## 全站唯一实现，两个入口共用（一期已定）
 * `from` 只影响三处：
 *   1. 面包屑第一段文字与返回目标（`teams` / `overview`）；
 *   2. 从队伍管理进入时，工具条追加「编辑队伍 / 管理成员」（归档队为「恢复队伍」）；
 *   3. 从队伍管理进入时，副标题追加档案信息（参与周期 / 创建时间 / 当前状态）。
 *
 * ## ⚠ 只剩一个区块是【占位空态】，不是漏做
 *   本期双周报 → W2-6
 *   （最近活动已于 2026-08-22 随 W2-7 接上真数据；团队 OKR 同日随 W2-5 接上。）
 * Hero 四格里的 OKR 进度 / 风险 KR / 14 天活动 / 本期双周报状态**都是真数据**。
 * ⚠ 接入时**只改这个文件里对应的那一段**，不要另起一个详情页。
 *
 * ## 团队 OKR 区块是【只读快照】
 * 维护入口在「团队 OKR」页（`#/okr/:teamId`）。⚠ 这里**不做行内编辑**——
 * 两处都能改，就会有一处漏掉乐观锁与「done 补齐 100」那条联动（一期已定的分工）。
 */
import type {
  Activity,
  ObjectiveView,
  ReportDetailView,
  TeamDetailView,
  TeamPersonRef,
} from '@shared-types';
import {
  activities as actApi,
  okr as okrApi,
  reports as reportApi,
  teams as teamApi,
} from '../api/index.js';
import { emptyHtml } from '../shared/common.js';
import { cut, esc, when } from '../shared/dom.js';
import { EIC, IC } from '../shared/icons.js';
import { TYPE_LABEL } from '../shared/nav.js';
import { state } from '../shared/state.js';
import * as meta from '../shared/meta.js';
import { archiveTeam, openMemberModal, openTeamModal, restoreTeam } from './teams.js';
import { definePage } from './types.js';

/* ===================== 小块 ===================== */

/**
 * 成员一行：姓名（+ 角色 / 停用标记）在左，**部门在右**。
 * ⚠ 2026-08-26 由一排 chip 改成两列名单——加上部门后 chip 一枚就有十几个字，
 *   半栏宽度下会折成三四行，且部门与姓名同一个视觉层级、读不出主次。
 *   名单形式的目的就是**让部门成为可以竖着扫下来的一列**，不要再改回 chip。
 * ⚠ 外部导师的 `dept` 记的是所属单位（后端 types/team.ts 已写明），照样放右列，不特殊处理。
 */
const mrow = (p: TeamPersonRef, tag: string): string =>
  `<div class="mrow${p.active ? '' : ' off'}">
     <b>${esc(p.name)}</b>${when(tag !== '', `<i>${esc(tag)}</i>`)}${when(!p.active, '<i class="off">已停用</i>')}
     <s title="${esc(p.dept)}">${esc(p.dept)}</s>
   </div>`;

const memberRow = (p: TeamPersonRef, leadId: string | null): string =>
  mrow(p, p.id === leadId ? '队长' : '');

/** 🔴 2026-09-14：以志愿者身份加入的正式成员标「志愿者 · T0X队员」，不能照 `type` 写成「正式成员」 */
const supLabel = (p: TeamPersonRef): string =>
  p.asVolunteer
    ? `${TYPE_LABEL.volunteer} · ${p.formalTeam ? `${p.formalTeam.code}队员` : TYPE_LABEL.member}`
    : TYPE_LABEL[p.type];

const supRow = (p: TeamPersonRef): string => mrow(p, supLabel(p));

/**
 * 两列名单。⚠ **人数为奇数时要补一个空占位格**：
 * 网格最后一格没有元素就没有边框，整个框会缺掉右下那一角（看起来像边框画漏了）。
 * 占位格靠 grid 的 stretch 自动撑到与同行等高，不用写死高度。
 */
const mlist = (rows: string[]): string =>
  `<div class="mlist">${rows.join('')}${when(rows.length % 2 === 1, '<div class="mrow pad"></div>')}</div>`;

/**
 * 本期双周报的状态徽标（Hero 第四格 + 区块标题右侧共用）。
 * 🔴 三条口径（红线 3）：
 *   · 归档队【不适用】——它已退出本期考核，不是「未提交」；
 *   · 本期【永远不可能逾期】（截止 = 结束当日 24:00），未提交只显示「距截止还有 N 天」；
 *   · 没有进行中的周期是【正常业务态】（空当期，红线 3.5），要有空态不能崩。
 */
function reportState(d: TeamDetailView): { tone: string; label: string } {
  if (d.team.archived) return { tone: 'arch', label: '不适用' };
  if (!d.current) return { tone: 'arch', label: '无进行中周期' };
  if (d.currentReport?.submitted) return { tone: 'ok', label: '已提交' };
  const n = d.daysToDue;
  return { tone: 'warn', label: n == null ? '未提交' : n > 0 ? `未提交 · 还有 ${n} 天` : '未提交 · 今天截止' };
}

/**
 * 团队 OKR 只读快照（一期 renderTeamDetail 里那一段，版式不变）。
 * ⚠ 三个数一个都不算：O 进度、KR 进度、风险条数全部由服务端下发（红线 4）。
 * ⚠ **O 进度为 null 时显示「—」不是 0%**：空目标不参与队伍平均，显示 0 会让人以为它在拖后腿。
 */
function okrSnapshot(objectives: ObjectiveView[]): string {
  if (!objectives.length) {
    return emptyHtml(EIC.flag, '该队尚未创建目标', 'O 与 KR 在「团队 OKR」模块维护', '', 'sm');
  }
  return objectives
    .map(
      (o) => `<div class="obj">
      <div class="obj-h"><i>O${o.no}</i><b>${esc(o.title)}</b>
        <u class="num">${o.progress == null ? '—' : o.progress + '%'}</u></div>
      ${o.krs
        .map((k) => {
          const cls = k.status === 'risk' ? 'r' : k.status === 'idle' ? 'n' : '';
          const lc = k.status === 'done' ? 'done' : k.status === 'risk' ? 'risk' : '';
          const lab =
            k.status === 'done' ? '已完成' : k.status === 'risk' ? '有风险' : k.status === 'idle' ? '未开始' : '进行中';
          return `<div class="krrow"><p>${esc(k.title)}</p>
            <span class="krb ${cls}"><i style="width:${k.progress}%"></i></span>
            <span class="krs ${lc}"><span class="num">${k.progress}%</span> · ${lab}</span></div>`;
        })
        .join('')}
    </div>`,
    )
    .join('');
}

/**
 * 本期双周报正文（W2-6 接入）。
 * ⚠ 这里是**只读快照**，维护入口在双周报模块——与团队 OKR 区块同一个分工：
 *   两处都能改，就会有一处漏掉版本校验与「历史期不同步 KR」那两条约束。
 * ⚠ 判「交没交」看的是 `report.report !== null`（服务端已按 `status==='submitted'` 过滤，
 *   非本队还会把未提交的正文置空），**不判正文字符串是否为空**（RULES §三）。
 */
function reportBodyHtml(r: ReportDetailView | null, d: TeamDetailView): string {
  /* ⚠ 判据是 `status === 'submitted'`，不是 `report !== null`：
     库里真的存在未提交的行（seed 会造，三期草稿更是常态），判空会把没交的正文当成果展示。 */
  const body = r?.report?.status === 'submitted' ? r.report : null;
  if (!body) {
    /* 2026-09-15 用户拍板：红色警示条改为中性空态。「还有 N 天 / 今天截止」已在区块标题的徽标里，
       这里不再重复；🔴 本期永远不可能逾期（红线 3），所以这里也没有任何警示语气。 */
    return emptyHtml(
      EIC.doc,
      '本期双周报尚未提交',
      d.current?.end ? `提交截止 ${d.current.end} 24:00` : '',
      '',
      'sm',
    );
  }
  const sec = (lab: string, txt: string, last = false): string =>
    `<div class="flab">${lab}</div><p class="ftext"${last ? ' style="margin-bottom:0"' : ''}>${esc(txt)}</p>`;
  return (
    sec('本期进展与成果', body.progress) +
    sec('下期计划', body.plan) +
    sec('问题与风险', body.risk) +
    sec('资源诉求', body.need, true) +
    `<div class="dmeta">
      <span>创建人：${esc(body.authorName ?? '—')}</span>
      <span>参与编辑：${body.editorNames.length ? body.editorNames.map(esc).join('、') : '—'}</span>
      <span>最近编辑：${esc(body.lastEditName ?? '—')}${
        body.lastEditAt ? ' · ' + esc(body.lastEditAt.slice(0, 16)) : ''
      }</span>
      ${when(r!.krSync.length > 0, `<span>本期同步关键结果：${r!.krSync.length} 条</span>`)}
    </div>`
  );
}

/**
 * 区块标题右侧的入口。
 * ⚠ 归档队【不给】：双周报模块的三页都不收它（它已退出考核）；
 *   历史双周报仍可在双周报模块按期次查到，只是不从这里进。
 * ⚠ 已提交 → 直接看详情；未提交 → 直接进编辑页，少点一次（一期同款）。
 * ⚠ 2026-08-26 版式调整：原来这里是两个按钮（本期 + 历史），用户拍板合成一个。
 *   历史双周报的入口**只剩双周报模块**——详情页不再直达上一期。
 */
function reportLinks(d: TeamDetailView): string {
  if (d.team.archived || !d.current) return '';
  const submitted = d.currentReport?.submitted === true;
  return `<button class="tbtn" style="margin-left:12px" data-nav="#/reports/p${d.current.no}/${esc(
    d.team.id,
  )}${submitted ? '' : '/edit'}">${submitted ? '查看详情' : '填写双周报'}</button>`;
}

/* ===================== 整页 ===================== */

/** 详情页的整页数据 = 队伍详情 + 该队最近活动（两个接口，一次 load 里并发取） */
export interface DetailData {
  view: TeamDetailView;
  /** 该队最近 3 条打卡（服务端已按日期倒序，这里只取前 3——截断是展示，不是口径） */
  recent: Activity[];
  /** 该队的 O/KR 树（只读快照）。⚠ 归档队照样能查，历史必须看得见 */
  objectives: ObjectiveView[];
  /**
   * 本期双周报正文（W2-6 接入）。
   * ⚠ 为 null 有三种情形，界面文案各不相同，**不要合并成一句「暂无」**：
   *   归档队（不在应交清单里）/ 没有进行中的周期（空当期，红线 3.5）/ 本期未提交。
   */
  report: ReportDetailView | null;
}

export function detailHtml(data: DetailData, from: string): string {
  const d = data.view;
  const t = d.team;
  const fromTeams = from === 'teams';
  const backHash = fromTeams ? '#/teams' : '#/';
  const st = reportState(d);

  /* 上一队 / 下一队：在【在用队伍】里前后翻。
     ⚠ 归档队不在 meta 的列表里（守卫要用那份列表判断能不能进 OKR / 双周报），
       所以归档队的翻页按钮是禁用的——这是对的，它已经不在考核序列里。 */
  const list = meta.activeTeams();
  const idx = list.findIndex((x) => x.id === t.id);
  const prev = idx > 0 ? list[idx - 1] : null;
  const next = idx >= 0 && idx < list.length - 1 ? list[idx + 1] : null;

  const fileInfo = fromTeams
    ? ` ｜ 参与周期 <b class="num">第 ${t.joinFrom} 期 ~ 第 ${t.joinTo} 期</b>` +
      ` ｜ 创建 <b class="num">${esc(t.createdAt.slice(0, 10))}</b>` +
      ` ｜ <b>${t.archived ? '已归档' : '生效'}</b>`
    : ` ｜ 参与周期 第 ${t.joinFrom} 期 ~ 第 ${t.joinTo} 期`;

  /* ⚠ 管理按钮只在「从队伍管理进入」时出现，且按角色给：
     编辑 / 恢复仅管理员，管理成员是本队队长或管理员。**这只是显示**，拦截在服务端（红线 4）。 */
  const admin = meta.isAdmin();
  const lead = meta.me()?.leadTeamId === t.id;
  const manageBtns = !fromTeams
    ? ''
    : (t.archived
        ? when(admin, `<button class="navbtn act" data-op="restore">恢复队伍</button>`)
        : when(admin, `<button class="navbtn act" data-op="edit">编辑队伍</button>`) +
          when(admin || lead, `<button class="navbtn act" data-op="mem">管理成员</button>`)) +
      when(admin || lead, '<span class="gapline"></span>');

  const members = d.roster.members;
  const sups = [...d.roster.volunteers, ...d.roster.mentors, ...d.roster.externals];

  /* 成果时间线：最新一期（倒序第一条）给全文；更早的期截断 80 字，点「展开」看全文。
     ⚠ 正文保留原换行（CSS pre-line）——周报常按「1、… 2、…」分行写，压成一行就读不出条目。
     展开 / 收起只是本地 class 切换（纯瞬时状态），重新渲染即复原。 */
  const TL_CUT = 80;
  const timeline = d.timeline.length
    ? d.timeline
        .map((x, i) => {
          const full = x.progress ?? '';
          const fold = i > 0 && full.length > TL_CUT;
          const body = fold
            ? `<p class="tl-short">${esc(cut(full, TL_CUT))}</p><p class="tl-full">${esc(full)}</p>
      <button class="tl-more" data-op="tl-more" aria-expanded="false">展开</button>`
            : `<p>${esc(full)}</p>`;
          return `
    <div class="tl-i">
      <div class="tlh"><b>${esc(x.periodName)}</b><s class="num">${esc(x.submitAt)}</s></div>
      ${body}
    </div>`;
        })
        .join('')
    : emptyHtml(EIC.doc, '暂无公开成果', '双周报提交后自动出现在这里', '', 'sm');

  return `
  <div class="crumb">
    <div class="crumb-path">
      <button data-nav="${backHash}">${fromTeams ? '队伍管理' : '运营总览'}</button><span>/</span><b>${esc(t.name)}</b>
    </div>
    <div class="crumb-tools">
      ${manageBtns}
      <button class="navbtn" ${prev ? `data-nav="#/${fromTeams ? 'teams' : 'overview'}/${esc(prev.id)}"` : 'disabled'}>${IC.left} 上一队</button>
      <span class="navpos num">${idx < 0 ? '—' : idx + 1} / ${list.length}</span>
      <button class="navbtn" ${next ? `data-nav="#/${fromTeams ? 'teams' : 'overview'}/${esc(next.id)}"` : 'disabled'}>下一队 ${IC.right}</button>
      <button class="navbtn" data-nav="${backHash}">返回${fromTeams ? '列表' : '总览'}<span class="kbd-hint">（Esc）</span></button>
    </div>
  </div>

  <div class="page">
    <div class="dhero">
      <span class="dhero-id num">${esc(t.code)}</span>
      <div class="dhero-main">
        <h1>${esc(t.name)}${when(t.archived, '<span class="dhero-badge">已归档</span>')}</h1>
        <p>${esc(t.topic)}${fileInfo}</p>
        ${when(
          t.archived,
          `<p class="dhero-note">该队已于 ${esc((t.archivedAt ?? '').slice(0, 10))} 归档，数据保留但不计入当期统计。</p>`,
        )}
      </div>
      <div class="dhero-stats">
        <div class="ds"><div class="l">OKR 进度</div><div class="v num">${t.okr}<small>%</small></div></div>
        <div class="ds"><div class="l">风险 KR</div><div class="v num ${t.risk ? 'warn' : ''}">${t.risk}<small> 条</small></div></div>
        <div class="ds"><div class="l">14天活动</div><div class="v num">${t.act14}<small> 条 / ${t.pax} 人次</small></div></div>
        <div class="ds"><div class="l">${esc(d.current?.name ?? '本期')}双周报</div>
          <div class="v" style="font-size:15px;padding-top:5px">
            <span class="badge ${st.tone}">${st.tone === 'ok' ? IC.check : IC.warn}${esc(st.label)}</span></div></div>
      </div>
    </div>

    <div class="dcols" style="margin-top:18px">
      <div>
        <div class="dblk">
          <div class="dblk-h"><h3>成员构成</h3><em>正式 ${members.length} 人 · 支持 ${d.supportCount} 人</em></div>
          <div class="dblk-b">
            ${
              members.length
                ? mlist(members.map((p) => memberRow(p, t.leadId)))
                : emptyHtml(EIC.team, '该队暂无正式成员', '通过「管理成员」从正式人员库添加', '', 'sm')
            }
            ${when(
              sups.length > 0,
              `<div class="rsub">支持人员（可跨队关联，只读权限）</div>
               ${mlist(sups.map(supRow))}`,
            )}
          </div>
        </div>
        <div class="dblk">
          <div class="dblk-h"><h3>最近活动</h3><em>近 14 天 ${t.act14} 条 · ${t.pax} 人次${
            data.recent.length ? ` · 显示最近 ${data.recent.length} 条` : ''
          }</em>
            <!-- ⚠ 归档队也给这个按钮：历史打卡要能查（一期需求全文「入口与出口」入口 2） -->
            <button class="tbtn" style="margin-left:12px" data-nav="#/checkins/${esc(t.id)}">查看全部打卡</button></div>
          <div class="dblk-b"><div class="dact">${
            data.recent.length
              ? data.recent
                  .map(
                    (a) => `
              <div class="dact-i">
                <span class="dd num">${esc(a.date.slice(5))}</span>
                <p>${esc(a.summary)}</p>
                <span class="atype">${a.memberIds.length + a.guests.length} 人次</span>
              </div>`,
                  )
                  .join('')
              : emptyHtml(EIC.cal, '近期没有活动记录', '该队的打卡记录会自动汇总到这里', '', 'sm')
          }</div></div>
        </div>
        <div class="dblk">
          <div class="dblk-h"><h3>团队 OKR</h3><em>${
            data.objectives.length
              ? `全部 KR 等权平均 = ${t.okr}%${t.risk ? ` · 风险 KR ${t.risk} 条` : ''}`
              : '暂无目标'
          }</em>
            <!-- ⚠ 归档队【不给】这个入口：它已退出本期考核，OKR 页里也没有它这一行，
                 点进去只会被守卫弹回默认队。历史 OKR 就在下面这个只读快照里。 -->
            ${when(!t.archived, `<button class="tbtn" style="margin-left:12px" data-nav="#/okr/${esc(t.id)}">管理 OKR</button>`)}</div>
          <div class="dblk-b">${okrSnapshot(data.objectives)}</div>
        </div>
      </div>
      <div>
        <div class="dblk">
      <div class="dblk-h"><h3>${esc(d.current?.name ?? '本期')}双周报</h3>
        <span class="dblk-meta ${st.tone === 'ok' ? 'ok' : st.tone === 'arch' ? 'mute' : 'bad'}">${
          st.tone === 'ok' ? IC.check : IC.info
        }${
          d.currentReport?.submitted
            ? `${esc(d.currentReport.submitterName ?? '')} ${esc((d.currentReport.submitAt ?? '').slice(0, 16))}${
                d.currentReport.overdueDays ? ` · 逾期 ${d.currentReport.overdueDays} 天提交` : ''
              }`
            : esc(st.label)
        }</span>
        ${when(!!d.current, `<em>周期 ${esc(d.current?.start ?? '')} ~ ${esc((d.current?.end ?? '').slice(5))}</em>`)}
        ${reportLinks(d)}</div>
      <div class="dblk-b">${
        t.archived
          ? emptyHtml(
              EIC.cal,
              '该队已归档，不在本期应提交清单中',
              '历史双周报仍完整保留，可在双周报模块查看',
              '',
              'sm',
            )
          : !d.current
            ? /* 🔴 空当期：正常业务态，文案里不许出现「错误 / 异常」（红线 3.5） */
              emptyHtml(
                EIC.cal,
                '当前没有进行中的周期',
                '周期由管理员临近再建，期与期之间允许有空当；建好后本区块自动恢复',
                '',
                'sm',
              )
            : reportBodyHtml(data.report, d)
      }</div>
        </div>
        <div class="dblk">
          <div class="dblk-h"><h3>成果时间线</h3><em>来自各期已提交双周报的「本期进展与成果」</em></div>
          <div class="dblk-b"><div class="tline">${timeline}</div></div>
        </div>
      </div>
    </div>
  </div>`;
}

/* ===================== 页面模块 ===================== */

export const teamDetailPage = definePage<DetailData>({
  skeleton: 'detail',

  async load({ route, signal }) {
    const id = route.view === 'detail' ? route.team : (state.team ?? '');
    /* ⚠ 本期双周报只在【有进行中的周期】且【队伍未归档】时才拉：
       空当期没有"本期"可言（红线 3.5），归档队不在应交清单里——
       这两种情况下打这个请求只会拿回一个注定不显示的空态。 */
    const cur = meta.currentPeriod();
    const archived = meta.teamById(id)?.archived === true;
    const [view, list, objectives, report] = await Promise.all([
      teamApi.detail(id, { signal }),
      // ⚠ span='all'：这一块要的是「最近几条」，不是「近 14 天的全部」——上面那行数字才是近 14 天
      actApi.listActivities({ teamId: id, span: 'all' }, { signal }),
      // ⚠ 归档队也拉：它的 OKR 只是不能改，不是不能看
      okrApi.objectivesOf(id, { signal }),
      cur && !archived ? reportApi.detail(id, cur.no, { signal }) : Promise.resolve(null),
    ]);
    return { view, recent: list.items.slice(0, 3), objectives, report };
  },

  html: (d, route) => detailHtml(d, route.view === 'detail' ? route.from : 'teams'),

  mounted() {
    document.querySelector('#main')?.scrollTo?.({ top: 0 });
  },

  onEvent(e, d) {
    const t = e.target;
    if (!(t instanceof Element)) return;

    /* Esc 返回列表（一期同款）。⚠ 有弹窗时 Esc 归弹窗，shared/events.ts 已经先处理过了 */
    if (e.type === 'keydown' && (e as KeyboardEvent).key === 'Escape') {
      location.hash = state.from === 'overview' ? '#/' : '#/teams';
      return;
    }
    if (e.type !== 'click') return;

    /* 三个管理入口【复用 pages/teams.ts 的同一份弹窗】。
       ⚠ 不在这里再写一遍表单：两处各一份，早晚有一处漏掉某条校验（一期的教训）。
       写完 afterTeamWrite 会 refreshCurrent()，停在详情页原地刷新。 */
    const op = t.closest<HTMLElement>('[data-op]')?.dataset.op;
    if (op === 'edit') return void openTeamModal(null, d.view.team);
    if (op === 'mem') return void openMemberModal(d.view.team);
    if (op === 'archive') return archiveTeam(d.view.team);
    if (op === 'restore') return restoreTeam(d.view.team);
    if (op === 'tl-more') {
      const item = t.closest('.tl-i');
      const btn = t.closest('[data-op]');
      const open = item?.classList.toggle('open') ?? false;
      btn?.setAttribute('aria-expanded', String(open));
      if (btn) btn.textContent = open ? '收起' : '展开';
    }
  },
});
